import { useState } from 'react';
import { ChevronLeft, ChevronRight, Heart, Share2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Catalog, CatalogItem } from '../../api/CataloguesAPI';
import noImage from '../../assets/images/null.png';
import { isVideoFile } from '../../shared/fileUtils';
import { formatCatalogPrice } from '../../shared/catalogLookbook';
import { toastService } from '../../services/ToastService';
import { PublicCatalogCtas } from './PublicCatalogCtas';
import PublicCatalogMedia, { CATALOG_MEDIA_SIZES } from './PublicCatalogMedia';

type PublicCatalogItemDetailProps = {
  catalog: Catalog;
  item: CatalogItem;
  saved: boolean;
  onBack: () => void;
  onImport: () => void;
  onToggleSave: () => void;
};

const PublicCatalogItemDetail = ({
  catalog,
  item,
  saved,
  onBack,
  onImport,
  onToggleSave,
}: PublicCatalogItemDetailProps) => {
  const { t } = useTranslation();
  const media = item.media && item.media.length > 0 ? item.media : [noImage];
  const [activeIndex, setActiveIndex] = useState(0);
  const current = media[activeIndex] || noImage;
  const isVideo = current !== noImage && isVideoFile(current);
  const price = formatCatalogPrice(item.price, item.currency);

  const go = (direction: 'prev' | 'next') => {
    setActiveIndex((index) => {
      if (direction === 'prev') {
        return (index - 1 + media.length) % media.length;
      }
      return (index + 1) % media.length;
    });
  };

  const shareLook = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: item.name, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      toastService.showToast(t('marketplace.catalogues.lookbook.share_copied'), 'success');
    } catch (error) {
      if ((error as { name?: string })?.name === 'AbortError') {
        return;
      }
      try {
        await navigator.clipboard.writeText(url);
        toastService.showToast(t('marketplace.catalogues.lookbook.share_copied'), 'success');
      } catch (copyError) {
        console.error('Failed to share catalog look', { itemUuid: item.uuid, error: copyError });
        toastService.showToast(t('marketplace.catalogues.lookbook.share_failed'), 'error');
      }
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:py-10">
      <button
        type="button"
        onClick={onBack}
        className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground transition hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" />
        {t('marketplace.catalogues.lookbook.back')}
      </button>

      <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
        <div className="space-y-3">
          <div className="relative aspect-[3/4] overflow-hidden rounded-2xl bg-as-pink-100 shadow-lg">
            <PublicCatalogMedia
              src={current}
              alt={item.name}
              className="h-full w-full object-cover"
              sizes={CATALOG_MEDIA_SIZES.detail}
              loading="eager"
              fetchPriority="high"
              controls={isVideo}
            />
            {media.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => go('prev')}
                  className="absolute left-3 top-1/2 -translate-y-1/2 lookbook-overlay-chip p-2"
                  aria-label={t('marketplace.catalogues.lookbook.prev_photo')!}
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={() => go('next')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 lookbook-overlay-chip p-2"
                  aria-label={t('marketplace.catalogues.lookbook.next_photo')!}
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </>
            )}
          </div>
          {media.length > 1 && (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {media.map((url, index) => (
                <button
                  key={`${url}-${index}`}
                  type="button"
                  onClick={() => setActiveIndex(index)}
                  className={`h-20 w-16 shrink-0 overflow-hidden rounded-lg border-2 ${
                    index === activeIndex ? 'border-as-pink-300' : 'border-transparent'
                  }`}
                >
                  <PublicCatalogMedia
                    src={url}
                    alt=""
                    className="h-full w-full object-cover"
                    sizes={CATALOG_MEDIA_SIZES.thumb}
                    variant="thumbnail"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="lg:sticky lg:top-24 lg:self-start">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-as-pink-500">
            {item.collectionName || catalog.name}
          </p>
          <h1
            className="mt-2 text-4xl font-light tracking-[0.12em] uppercase text-foreground"
            style={{ fontFamily: 'trajan-pro-regular, Inter, serif' }}
          >
            {item.name}
          </h1>
          {item.designer && (
            <p className="mt-3 text-sm text-muted-foreground">
              {t('marketplace.catalogues.details.designer')}: {item.designer}
            </p>
          )}
          <p className="mt-6 text-2xl font-light text-foreground">
            {price || t('marketplace.catalogues.lookbook.price_in_salon')}
          </p>
          {item.description && (
            <p className="mt-6 whitespace-pre-wrap text-base leading-relaxed text-muted-foreground">
              {item.description}
            </p>
          )}
          <div className="mt-8 hidden md:block">
            <PublicCatalogCtas catalog={catalog} onImport={onImport} fromItem layout="stack" />
          </div>
          <div className="mt-6 flex items-center gap-3">
            <button
              type="button"
              onClick={onToggleSave}
              className={`inline-flex h-11 items-center gap-2 rounded-full border px-4 text-sm font-semibold btn-hover-motion ${
                saved
                  ? 'border-as-pink-300 bg-as-pink-300 text-white'
                  : 'border-as-pink-200 bg-white/70 text-as-pink-500 hover:bg-as-pink-100 dark:bg-white/10 dark:hover:bg-white/15'
              }`}
            >
              <Heart className={`h-4 w-4 ${saved ? 'fill-current' : ''}`} />
              {saved ? t('marketplace.catalogues.lookbook.saved') : t('marketplace.catalogues.lookbook.save')}
            </button>
            <button
              type="button"
              onClick={shareLook}
              className="inline-flex h-11 items-center gap-2 rounded-full border border-as-pink-300 bg-white/70 px-4 text-sm font-semibold text-as-pink-500 btn-hover-motion hover:bg-as-pink-100 dark:bg-white/10 dark:hover:bg-white/15"
            >
              <Share2 className="h-4 w-4" />
              {t('marketplace.catalogues.lookbook.share')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicCatalogItemDetail;
